// src/app/argent/rapport.js — le rapport mensuel du propriétaire.
// CHEMIN ARGENT. Déplacé octet pour octet depuis src/App.jsx (P-3.5, squelette,
// partie 2) : seuls changent l'emballage (signature, retour) et les imports.

import { daysInMonth, MONTHS_SHORT } from '../../lib/format'
import { LOGO_B64 } from '../../assets/logo'
import { getDegs, isFactureEtat } from '../../lib/incidents-helpers'
import { resoudreCommission } from '../../lib/commission-taux-date'
import { commissionLigneFacture } from '../../lib/commission-lignes'
import { echapperHtml as esc } from '../../lib/echapper-html'
import { ouvrirApercuIsole } from '../../lib/apercu-isole'

export function rapportProprio({ cfg, argentLectureRatee, incidents, month, y, m, monthLabel, calc }) {
  const genRapport = (prop, log, mode = 'email') => {
    const c = calc(log.id, log.taux)
    // Lot taux-date : barème EN VIGUEUR au mois affiché (`month`, clé AAAA-MM).
    // Sans `tauxHistorique`, `baremeSrc` EST `log` — rendu strictement inchangé.
    const baremeRapport = resoudreCommission(log, month)
    const baremeSrc = baremeRapport.historise ? baremeRapport : log
    // PR #169b (ADR-106, Option A Q3 Dimitri) : listing inclut résas direct
    // internes FBZ. `c.resas` = KV V1 ∪ pseudo-resas pricing_blocks via mergeCalc.
    // Le mapping pseudo (source='pricing_block_internal', plateforme='direct',
    // voyageur=guest_first_name + guest_last_name) est fait dans calcDirectInternePure.
    const resas = c.resas || []
    // Option B : occupation = nuits RÉELLES occupées ce mois (c.nuitsOccup),
    // pas le total des nuits des résas imputées (c.nuits). RevPAR inchangé
    // (dénominateur = jours dispo, numérateur = CA ; guard d'activité conservé).
    const occ = daysInMonth(y,m)>0?Math.round(c.nuitsOccup/daysInMonth(y,m)*100):0
    const revpar = c.nuits>0?Math.round(c.ca/daysInMonth(y,m)*100)/100:0

    // Données annuelles
    const months12 = Array.from({length:12},(_,i)=>{
      const mk2=`${y}-${String(i+1).padStart(2,'0')}`
      const c2=calc(log.id,log.taux,mk2)
      return {label:MONTHS_SHORT[i],ca:c2.ca,nuits:c2.nuits,comm:c2.comm}
    })

    // Incidents du mois (Aircover/Caution)
    const incidentsMois = incidents.filter(i=>{
      const d=new Date(i.createdAt||0)
      return d.getFullYear()===y&&(d.getMonth()+1)===m&&i.logId===log.id&&getDegs(i).some(d=>d.statutMenage==="Non récupérable")
    })

    // Distribution plateformes (Lot 4 : 'manuel' inclus pour que les résas
    // manuelles V2 comptent dans la répartition au lieu d'être exclues en silence).
    const plDist = {airbnb:0,booking:0,direct:0,manuel:0}
    resas.forEach(r=>{ if(plDist[r.plateforme]!==undefined) plDist[r.plateforme]+=(r.montant||0) })
    const plTotal = Object.values(plDist).reduce((s,v)=>s+v,0)||1

    // Faille N1 (lot L5) : toute valeur venue des données passe par `esc`
    // (src/lib/echapper-html.js) ; les nombres du script des graphiques par
    // Number(). Garde : src/lib/apercu-isole.test.js.
    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Rapport ${esc(log.nom)} — ${esc(monthLabel)}</title>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Raleway:wght@400;500;600;700&display=swap" rel="stylesheet">
<script src="https://cdnjs.cloudflare.com/ajax/libs/Chart.js/3.9.1/chart.min.js"></script>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  @page{size:A4;margin:0.5cm}
  body{font-family:'Raleway',sans-serif;background:#eef2f7;color:#333;padding:20px}
  .page{background:#fff;max-width:760px;margin:0 auto;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,.1)}
  .print-btn{position:fixed;top:20px;right:20px;background:#215387;color:#fff;border:none;border-radius:8px;padding:10px 18px;font-size:13px;font-weight:700;cursor:pointer;font-family:'Raleway',sans-serif;box-shadow:0 4px 12px rgba(33,83,135,.3);z-index:9999}
  .print-btn:hover{background:#1a3f66}
  .header{background:linear-gradient(135deg,#1a3f66,#215387,#3a72a8);color:#fff;padding:28px 36px;display:flex;justify-content:space-between;align-items:center}
  .logo-area{display:flex;align-items:center;gap:14px}
  .logo{width:48px;height:48px;border-radius:50%;object-fit:cover;border:2px solid rgba(255,255,255,.3)}
  .company h1{font-family:'Playfair Display',serif;font-size:20px}
  .company p{font-size:11px;opacity:.7;margin-top:2px}
  .header-right{text-align:right}
  .header-right h2{font-family:'Playfair Display',serif;font-size:16px}
  .header-right p{font-size:11px;opacity:.7;margin-top:3px}
  .body{padding:28px 36px}
  .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px}
  .kpi{background:#f8fafd;border-radius:10px;padding:16px;text-align:center}
  .kpi.hi{background:linear-gradient(135deg,#1a3f66,#215387);color:#fff}
  .kpi-val{font-family:'Playfair Display',serif;font-size:22px;font-weight:700;margin-bottom:4px}
  .kpi-label{font-size:10px;text-transform:uppercase;letter-spacing:.4px;opacity:.7}
  .section-title{font-family:'Playfair Display',serif;font-size:14px;color:#215387;font-weight:700;margin-bottom:12px;padding-bottom:6px;border-bottom:2px solid #eef2f7}
  .charts{display:grid;grid-template-columns:2fr 1fr;gap:16px;margin-bottom:24px}
  .chart-box{background:#f8fafd;border-radius:10px;padding:16px}
  .chart-box h3{font-size:11px;color:#888;text-transform:uppercase;letter-spacing:.4px;margin-bottom:12px;font-weight:700}
  table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:24px}
  th{background:#f0f4f8;padding:9px 12px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.4px;color:#555;font-weight:700}
  th.r,td.r{text-align:right}
  td{padding:9px 12px;border-bottom:1px solid #f0f4f8;color:#444}
  .incident-box{background:#fdf2f2;border-radius:10px;padding:16px;margin-bottom:24px;border-left:4px solid #c0392b}
  .incident-box h3{color:#c0392b;font-size:13px;font-weight:700;margin-bottom:10px}
  .footer{text-align:center;font-size:10px;color:#bbb;padding:16px;border-top:1px solid #f0f4f8}
  @media print{
    body{padding:0;background:#fff}
    .page{box-shadow:none;border-radius:0;max-width:100%}
    .print-btn{display:none!important}
    *{-webkit-print-color-adjust:exact;color-adjust:exact;print-color-adjust:exact}
  }
</style>
</head>
<body>
${mode==='preview'?`<button class="print-btn" onclick="window.print()">🖨️ Imprimer / Enregistrer en PDF</button>`:''}
<div class="page">
  <div class="header">
    <div class="logo-area">
      <img src="data:image/png;base64,${LOGO_B64}" class="logo" alt="logo"/>
      <div class="company">
        <h1>FULLBNBZEN</h1>
        <p>Rapport mensuel</p>
      </div>
    </div>
    <div class="header-right">
      <h2>${esc(log.nom)}</h2>
      <p>${esc(prop.nom)} · Commission ${esc(baremeSrc.taux)}%</p>
      <p style="margin-top:4px">${esc(monthLabel)}</p>
    </div>
  </div>
  <div class="body">
    <div class="kpis">
      <div class="kpi"><div class="kpi-val">${esc(c.nuitsOccup)}</div><div class="kpi-label">Nuits louées</div></div>
      <div class="kpi"><div class="kpi-val">${occ}%</div><div class="kpi-label">Occupation</div></div>
      <div class="kpi hi"><div class="kpi-val">${c.ca.toFixed(0)}€</div><div class="kpi-label">CA généré</div></div>
      <div class="kpi"><div class="kpi-val">${revpar.toFixed(0)}€</div><div class="kpi-label">RevPAR</div></div>
    </div>

    <div class="charts">
      <div class="chart-box">
        <h3>CA mensuel ${y}</h3>
        <canvas id="caChart" height="120"></canvas>
      </div>
      <div class="chart-box">
        <h3>Plateformes</h3>
        <canvas id="plChart" height="120"></canvas>
      </div>
    </div>

    <div class="section-title">Réservations du mois</div>
    <table>
      <thead><tr><th>Voyageur</th><th>Plateforme</th><th class="r">Nuits</th><th class="r">Montant</th><th class="r">Ménage</th><th class="r">Comm.</th></tr></thead>
      <tbody>
        ${[...resas].sort((a,b) => (a.checkIn||a.start_date||a.date||'').localeCompare(b.checkIn||b.start_date||b.date||'')).map(r=>{
          // Base commission = montant − ménage (taxe trackée séparément, cf. ADR-048)
          // NQAb-1 fix : commission dégressive par palier (taux de la ligne)
          // sinon le listing affiche commission au taux fixe alors que le total agrégé
          // (calc()) applique le palier — divergence somme lignes ≠ total commission.
          // 2026-09-30 : la ligne était NON arrondie (toFixed à l'affichage)
          // alors que le total lit c.comm — les lignes ne s'additionnaient pas,
          // le défaut corrigé dans la facture le 05/08. Désormais la MÊME
          // fonction que la facture et que calc() : arrondie par ligne.
          const { comm } = commissionLigneFacture(r, { baremeSrc, tauxFixe: baremeSrc.taux })
          const plColors={airbnb:'#FF5A5F',booking:'#003580',direct:'#2d7a3a'}
          const plLabels={airbnb:'Airbnb',booking:'Booking',direct:'Direct'}
          const pl=r.plateforme
          // PR #169b — marquage "Résa direct FBZ" pour pricing_block_internal (Option A Q3)
          const isDirectInterne = r.source === 'pricing_block_internal'
          const voyageurLabel = isDirectInterne
            ? `${esc(r.voyageur||'—')} <span style="background:#fff7e6;color:#92400e;border-radius:3px;padding:1px 6px;font-size:9px;font-weight:700;margin-left:4px">Résa direct FBZ</span>`
            : esc(r.voyageur||'—')
          return `<tr>
            <td style="font-weight:600">${voyageurLabel}</td>
            <td><span style="background:${plColors[pl]||'#888'}22;color:${plColors[pl]||'#888'};border-radius:4px;padding:2px 8px;font-size:10px;font-weight:700">${esc(plLabels[pl]||pl)}</span></td>
            <td class="r">${esc(r.nuits||0)}</td>
            <td class="r">${(r.montant||0).toFixed(2)}&nbsp;€</td>
            <td class="r">${(r.menage||0).toFixed(2)}&nbsp;€</td>
            <td class="r" style="font-weight:700;color:#215387">${comm.toFixed(2)}&nbsp;€</td>
          </tr>`
        }).join('')}
        <tr style="background:#f0f4f8;font-weight:700">
          <td colspan="3">Total</td>
          <td class="r">${c.ca.toFixed(2)}&nbsp;€</td>
          <td class="r">${c.men.toFixed(2)}&nbsp;€</td>
          <td class="r" style="color:#215387">${c.comm.toFixed(2)}&nbsp;€</td>
        </tr>
      </tbody>
    </table>

    <div class="section-title">Récapitulatif annuel ${y}</div>
    <table>
      <thead><tr><th>Mois</th><th class="r">Nuits</th><th class="r">CA</th><th class="r">Comm.</th></tr></thead>
      <tbody>
        ${months12.map((mm,i)=>`<tr style="${i+1===m?'background:#eef2f7;font-weight:700':''}">
          <td>${mm.label}</td>
          <td class="r">${esc(mm.nuits||'—')}</td>
          <td class="r">${mm.ca>0?mm.ca.toFixed(2)+'&nbsp;€':'—'}</td>
          <td class="r" style="color:#215387">${mm.comm>0?mm.comm.toFixed(2)+'&nbsp;€':'—'}</td>
        </tr>`).join('')}
      </tbody>
    </table>

    ${(()=>{
      const itemsFbz = []
      const itemsInfo = []
      incidentsMois.forEach(i=>{
        getDegs(i).filter(d=>d.statutMenage==="Non récupérable").forEach(d=>{
          const label = `${d.nombre>1?d.nombre+'× ':''}${d.type}${d.precision?' ('+d.precision+')':''}`
          // Chantier ⑥ — 5 états : refacturé au proprio (« Refacturé propriétaire »
          // OU « AirCover + refacturé ») → colonne « à rembourser » au BRUT. Le
          // reste (Sans coût / Achat FULLBNBZEN / Remboursé AirCover) = info.
          if(isFactureEtat(d)) itemsFbz.push({label,remplaceLe:d.remplaceLe,cout:parseFloat(d.cout)||0,voyageur:i.voyageur,aircoverMontant:parseFloat(d.aircoverMontant)||0})
          else itemsInfo.push({label,voyageur:i.voyageur,aircoverMontant:parseFloat(d.aircoverMontant)||0})
        })
      })
      const totalFbz = itemsFbz.reduce((s,x)=>s+x.cout,0)
      if(!itemsFbz.length&&!itemsInfo.length) return ''
      return `
    <div class="incident-box">
      <h3>⚠️ Suivi incidents — ${incidentsMois.length} incident${incidentsMois.length>1?'s':''} ce mois</h3>
      ${itemsFbz.length>0?`
      <p style="font-size:11px;font-weight:700;color:#215387;margin-bottom:6px;text-transform:uppercase;letter-spacing:.4px">Remplacements FULLBNBZEN — à rembourser</p>
      <table style="margin-bottom:12px">
        <thead><tr><th>Item</th><th>Voyageur</th><th>Remplacé le</th><th class="r">Coût</th><th class="r">Aircover perçu</th></tr></thead>
        <tbody>
          ${itemsFbz.map(x=>`<tr>
            <td style="font-weight:700;color:#c0392b">${esc(x.label)}</td>
            <td>${esc(x.voyageur||'—')}</td>
            <td style="color:${x.remplaceLe?'#2d7a3a':'#d97706'}">${x.remplaceLe?new Date(x.remplaceLe).toLocaleDateString('fr-FR'):'En attente'}</td>
            <td class="r" style="font-weight:700">${x.cout>0?x.cout.toFixed(2)+'&nbsp;€':'—'}</td>
            <td class="r" style="color:#1565c0">${x.aircoverMontant>0?x.aircoverMontant.toFixed(2)+'&nbsp;€':'—'}</td>
          </tr>`).join('')}
          <tr style="background:#f0f4f8;font-weight:700">
            <td colspan="3">Total à rembourser FULLBNBZEN</td>
            <td class="r" style="color:#215387">${totalFbz.toFixed(2)}&nbsp;€</td>
          </tr>
        </tbody>
      </table>`:''}
      ${itemsInfo.length>0?`
      <p style="font-size:11px;font-weight:700;color:#888;margin-bottom:6px;text-transform:uppercase;letter-spacing:.4px">Dégâts signalés — remboursement Aircover direct</p>
      <table style="margin-bottom:0">
        <thead><tr><th>Item</th><th>Voyageur</th><th class="r">Aircover perçu</th></tr></thead>
        <tbody>
          ${itemsInfo.map(x=>`<tr>
            <td style="font-weight:700;color:#c0392b">${esc(x.label)}</td>
            <td>${esc(x.voyageur||'—')}</td>
            <td class="r" style="color:#1565c0">${x.aircoverMontant>0?x.aircoverMontant.toFixed(2)+'&nbsp;€':'—'}</td>
          </tr>`).join('')}
        </tbody>
      </table>`:''}
    </div>`
    })()}

    <div class="footer">FULLBNBZEN SAS · SIRET ${esc(cfg.co?.siret||'927 602 805 00016')} · Rapport généré le ${new Date().toLocaleDateString('fr-FR')}</div>
  </div>
</div>
<script>
  const caData = ${JSON.stringify(months12.map(m=>m.ca))}
  const caLabels = ${JSON.stringify(months12.map(m=>m.label))}
  const currentMonth = ${m-1}
  new Chart(document.getElementById('caChart'),{
    type:'bar',
    data:{
      labels:caLabels,
      datasets:[{
        data:caData,
        backgroundColor:caData.map((_,i)=>i===currentMonth?'#215387':i<currentMonth?'#87b8e6':'#dde8f5'),
        borderRadius:4
      }]
    },
    options:{plugins:{legend:{display:false}},scales:{y:{beginAtZero:true,ticks:{font:{size:10}}},x:{ticks:{font:{size:10}}}}}
  })
  new Chart(document.getElementById('plChart'),{
    type:'doughnut',
    data:{
      labels:['Airbnb','Booking','Direct'],
      datasets:[{data:[${Number(plDist.airbnb)},${Number(plDist.booking)},${Number(plDist.direct)}],backgroundColor:['#FF5A5F','#003580','#2d7a3a'],borderWidth:2}]
    },
    options:{plugins:{legend:{position:'bottom',labels:{font:{size:10},padding:8}}}}
  })
  // Aperçu isolé (lot L5) : la coque ne peut pas lire ce document (origine
  // opaque) ; il lui donne sa hauteur pour que Cmd+P imprime toutes les pages.
  window.addEventListener('load',function(){if(window.parent!==window)window.parent.postMessage({fbzHauteurDocument:document.documentElement.scrollHeight},'*')})
</script>
</body>
</html>`
    return {html}
  }

  // Génération rapport — ouvre l'aperçu HTML dans un nouvel onglet, l'utilisateur imprime via le bouton flottant (window.print) pour obtenir un PDF natif navigateur
  const doGenRapport = (prop, log) => {
    // F13 (tour 3) : données de facturation non lues → aucun rapport imprimable
    // avec CA, commission et occupation calculés sur un vide.
    if (argentLectureRatee) { console.warn('[rapport] refusé : données de facturation non lues'); return }
    const {html} = genRapport(prop, log, 'preview')
    // Faille N1 : cadre isolé ; les graphiques ont besoin de leurs scripts,
    // qui tournent dans une origine opaque (aucun accès à l'appli).
    ouvrirApercuIsole(html, { scripts: true, titre: `Rapport ${log.nom} — ${monthLabel}` })
  }
  return { doGenRapport }
}
